import { z } from "zod";

export const slugSchema = z
  .string()
  .min(3, "Slug must be at least 3 characters")
  .max(50, "Slug must be 50 characters or fewer")
  .regex(
    /^[a-z0-9-]+$/,
    "Slug may only contain lowercase letters, numbers, and hyphens",
  );

export const createTenantSchema = z.object({
  slug: slugSchema,
  name: z.string().min(2).max(120),
  venueName: z.string().min(2).max(120).optional(),
});
export type CreateTenantInput = z.infer<typeof createTenantSchema>;

// Each mandatory consent is captured as a literal-true boolean; this
// rejects the form at validation if any is unchecked, before we ever
// reach the auth call. Marketing opt-in is a plain boolean, defaulting
// to false (POPIA + GDPR — no pre-ticked optional consents).
export const signUpSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, "At least 8 characters"),
  fullName: z.string().min(2).max(120),
  consentAuthoriseToBind: z.literal(true, {
    errorMap: () => ({ message: "You must confirm you can bind your organisation." }),
  }),
  consentMsaAndDpa: z.literal(true, {
    errorMap: () => ({ message: "You must accept the MSA and Data Protection Schedule." }),
  }),
  consentSubProcessors: z.literal(true, {
    errorMap: () => ({ message: "You must authorise Vertu's Sub-Operators and cross-border transfers." }),
  }),
  consentGuestPrivacyNotice: z.literal(true, {
    errorMap: () => ({ message: "You must commit to publishing a guest-facing privacy notice." }),
  }),
  consentMarketing: z.boolean().default(false),
});
export type SignUpInput = z.infer<typeof signUpSchema>;

export const signInSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});
export type SignInInput = z.infer<typeof signInSchema>;

export const serviceSchema = z.object({
  slug: slugSchema,
  name: z.string().min(2).max(120),
  service_type: z.enum([
    "accommodation",
    "dining",
    "events",
    "spa",
    "picnic",
    "tasting",
    "club",
    "tour",
    "custom",
  ]),
  tagline: z.string().max(200).optional(),
  description: z.string().max(2000).optional(),
  is_enabled: z.boolean().default(true),
  is_featured: z.boolean().default(false),
  display_order: z.number().int().min(0).default(0),
  hero_image_url: z.string().url().optional().or(z.literal("")),
});
export type ServiceInput = z.infer<typeof serviceSchema>;

// ---------------------------------------------------------------------------
// Widget theme — structured overrides for the embeddable widget runtime.
// Every field is optional; missing values fall back to tenant/brand/defaults
// via the runtime theme resolver.
// ---------------------------------------------------------------------------
const hexColor = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, "Use a 6-digit hex colour like #1A1F51");

export const RADIUS_PRESETS = ["sharp", "soft", "rounded", "pill"] as const;
export type RadiusPreset = (typeof RADIUS_PRESETS)[number];

// How far a brand's colours reach in the widget: just the active tab's hero
// header, or the whole active-tab panel (prices, highlights, accents). Only
// affects multi-brand widgets where each tab carries its own brand colours.
export const BRAND_TINT_SCOPES = ["header", "panel"] as const;
export type BrandTintScope = (typeof BRAND_TINT_SCOPES)[number];

// The widget's header bar and image-less panel headers: the brand gradient
// and glow (default), or one solid primary colour.
export const HEADER_FILLS = ["gradient", "solid"] as const;
export type HeaderFill = (typeof HEADER_FILLS)[number];

// The venue's name in the widget header: the display font as typed
// (default), or the body font in bold capitals, like the tagline under it.
export const VENUE_NAME_STYLES = ["display", "caps"] as const;
export type VenueNameStyle = (typeof VENUE_NAME_STYLES)[number];

// The "Open today" hours pill: status colours (green open, default), or the
// brand's own (subtle background, primary-colour text). Closed stays red.
export const HOURS_PILL_STYLES = ["status", "brand"] as const;
export type HoursPillStyle = (typeof HOURS_PILL_STYLES)[number];

export const FONT_PAIRS = [
  "vertu", // Fraunces + Inter (default — Vertu brand)
  "heritage", // Cormorant Garamond + Inter
  "modern", // Inter + Inter
  "editorial", // Playfair Display + Inter
  "contemporary", // Fraunces + Manrope
  "minimal", // DM Sans + DM Sans
  "refined", // Roboto Serif + Inter Tight
] as const;
export type FontPair = (typeof FONT_PAIRS)[number];

// Font-size presets — applied as a scale multiplier in the widget (see
// SIZE_MAP in apps/widget/src/theme.ts). Two independent scales: action
// buttons, and everything-else text (titles, descriptions, body).
export const TEXT_SIZE_PRESETS = ["small", "default", "large", "xlarge"] as const;
export type TextSizePreset = (typeof TEXT_SIZE_PRESETS)[number];
export const TEXT_SIZE_LABELS: Record<TextSizePreset, string> = {
  small: "Small",
  default: "Default",
  large: "Large",
  xlarge: "Extra-large",
};

export const widgetThemeSchema = z.object({
  primaryColor: hexColor.optional(),
  accentColor: hexColor.optional(),
  // Accent variants — used for hover states, highlight backgrounds, and
  // soft chips/badges in v2. Tenants who only set accentColor get sensible
  // CSS color-mix fallbacks at render time (see styles.css).
  accentLight: hexColor.optional(),
  accentSoft: hexColor.optional(),
  creamColor: hexColor.optional(),
  warmCreamColor: hexColor.optional(),
  warmCreamDark: hexColor.optional(),
  charcoal: hexColor.optional(),
  slateMuted: hexColor.optional(),
  slateLight: hexColor.optional(),
  border: hexColor.optional(),
  borderLight: hexColor.optional(),
  success: hexColor.optional(),
  danger: hexColor.optional(),
  // Primary CTA — e.g. "Add to booking", "Pay with Paystack". Tenants who
  // leave these blank inherit Vertu's default purple-on-white.
  ctaBackground: hexColor.optional(),
  ctaText: hexColor.optional(),
  radius: z.enum(RADIUS_PRESETS).optional(),
  fonts: z.enum(FONT_PAIRS).optional(),
  // Font-size scale presets — buttons vs everything-else text. Omitted /
  // "default" leaves the platform sizes unchanged.
  buttonTextSize: z.enum(TEXT_SIZE_PRESETS).optional(),
  bodyTextSize: z.enum(TEXT_SIZE_PRESETS).optional(),
  heroOverlayOpacity: z.number().min(0).max(1).optional(),
  // Reach of the active tab's brand colours: "header" (hero only, default) or
  // "panel" (the whole active-tab surface).
  brandTintScope: z.enum(BRAND_TINT_SCOPES).optional(),
  headerFill: z.enum(HEADER_FILLS).optional(),
  venueNameStyle: z.enum(VENUE_NAME_STYLES).optional(),
  hoursPillStyle: z.enum(HOURS_PILL_STYLES).optional(),
});
export type WidgetTheme = z.infer<typeof widgetThemeSchema>;

export const RADIUS_MAP: Record<
  RadiusPreset,
  { sm: string; md: string; lg: string }
> = {
  sharp: { sm: "0px", md: "2px", lg: "4px" },
  soft: { sm: "6px", md: "10px", lg: "16px" },
  rounded: { sm: "8px", md: "12px", lg: "20px" },
  pill: { sm: "12px", md: "18px", lg: "28px" },
};

export const FONT_MAP: Record<FontPair, { display: string; body: string; label: string }> = {
  vertu: {
    label: "Vertu — Fraunces × Inter",
    display: "'Fraunces', Georgia, serif",
    body: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  heritage: {
    label: "Heritage — Cormorant Garamond × Inter",
    display: "'Cormorant Garamond', 'Times New Roman', serif",
    body: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  modern: {
    label: "Modern — Inter everywhere",
    display: "'Inter', -apple-system, sans-serif",
    body: "'Inter', -apple-system, sans-serif",
  },
  editorial: {
    label: "Editorial — Playfair × Inter",
    display: "'Playfair Display', Georgia, serif",
    body: "'Inter', -apple-system, sans-serif",
  },
  contemporary: {
    label: "Contemporary — Fraunces × Manrope",
    display: "'Fraunces', Georgia, serif",
    body: "'Manrope', -apple-system, sans-serif",
  },
  minimal: {
    label: "Minimal — DM Sans everywhere",
    display: "'DM Sans', -apple-system, sans-serif",
    body: "'DM Sans', -apple-system, sans-serif",
  },
  refined: {
    label: "Refined — Roboto Serif × Inter Tight",
    display: "'Roboto Serif', Georgia, serif",
    body: "'Inter Tight', -apple-system, sans-serif",
  },
};

export const widgetConfigSchema = z.object({
  slug: slugSchema,
  name: z.string().min(2).max(120),
  description: z.string().max(500).optional(),
  enabled_service_ids: z.array(z.string().uuid()).default([]),
  default_service_id: z.string().uuid().nullable().optional(),
  brand_id: z.string().uuid().nullable().optional(),
  theme: z.record(z.unknown()).default({}),
  settings: z.record(z.unknown()).default({}),
  is_active: z.boolean().default(true),
});
export type WidgetConfigInput = z.infer<typeof widgetConfigSchema>;

export const brandSchema = z.object({
  slug: slugSchema,
  name: z.string().min(2).max(120),
  tagline: z.string().max(200).optional(),
  description: z.string().max(2000).optional(),
  logo_url: z.string().url().optional().or(z.literal("")),
  primary_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Must be a hex colour like #1A1F51")
    .optional()
    .or(z.literal("")),
  accent_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Must be a hex colour like #B8923E")
    .optional()
    .or(z.literal("")),
  // Header "Reserve"/"Book" button colour for this brand's tabs. Blank inherits
  // the widget's shared CTA colour.
  hero_cta_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Must be a hex colour like #B8923E")
    .optional()
    .or(z.literal("")),
  hero_cta_text_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Must be a hex colour like #FFFFFF")
    .optional()
    .or(z.literal("")),
  hero_image_url: z.string().url().optional().or(z.literal("")),
  website_url: z.string().url().optional().or(z.literal("")),
  contact_email: z.string().email().optional().or(z.literal("")),
  contact_phone: z.string().optional(),
  is_active: z.boolean().default(true),
  display_order: z.number().int().min(0).default(0),
});
export type BrandInput = z.infer<typeof brandSchema>;

export const serviceUnitSchema = z.object({
  slug: slugSchema,
  name: z.string().min(2).max(120),
  description: z.string().max(1000).optional(),
  capacity: z.number().int().min(1).optional(),
  base_price: z.number().min(0).optional(),
  currency: z.string().length(3).default("ZAR"),
  inventory_count: z.number().int().min(0).optional(),
  image_url: z.string().url().optional().or(z.literal("")),
  is_enabled: z.boolean().default(true),
  display_order: z.number().int().min(0).default(0),
});
export type ServiceUnitInput = z.infer<typeof serviceUnitSchema>;
